import type { MessagingAccountCreateRequest } from '@arhs-fleetback/types/entities/messaging-accounts';

import { MESSAGING_ACCOUNTS_API_URL, MESSAGING_ACCOUNTS_FILTER_URL } from 'constants/ApiConstants';
import { PaginationType } from 'constants/Global';
import type { ApiError, MetaAction } from 'modules/global/generic.types';
import * as types from 'modules/messagingAccounts/actionTypes';
import { AVAILABLE_PAGE_SIZE, DEFAULT_SORT } from 'modules/messagingAccounts/constants';
import type * as actionTypes from 'modules/messagingAccounts/types';

export const fetchMessagingAccounts = (params: actionTypes.FetchMessagingAccountsParams, meta: MetaAction = {}): actionTypes.FetchMessagingAccountsAction => ({
  type: types.FETCH_MESSAGING_ACCOUNTS,
  payload: {
    request: {
      method: 'GET',
      url: MESSAGING_ACCOUNTS_FILTER_URL,
      params: { ...params, projection: 'page' as const, paginationType: PaginationType.SLICE },
    },
  },
  meta: { '@app-request': true, ...meta },
});

export const setDashboardFilter = (field: string, value: unknown, meta: MetaAction = {}): actionTypes.SetMessagingAccountsFilterAction => ({
  type: types.SET_DASHBOARD_FILTER,
  payload: { field, value },
  meta,
});

export const clearDashboardFilters = (meta: MetaAction = {}): actionTypes.ClearMessagingAccountsFiltersAction => ({
  type: types.CLEAR_DASHBOARD_FILTERS,
  meta,
});

export const fetchMessagingAccount = (accountId: string, meta: MetaAction = {}): actionTypes.FetchMessagingAccountAction => ({
  type: types.FETCH_MESSAGING_ACCOUNT,
  payload: {
    request: {
      method: 'GET',
      url: `${MESSAGING_ACCOUNTS_API_URL}/${accountId}`,
    },
  },
  meta: { '@app-request': true, ...meta },
});

export const createMessagingAccount = (
  account: MessagingAccountCreateRequest,
  meta: MetaAction = {}
): actionTypes.CreateMessagingAccountAction => ({
  type: types.CREATE_MESSAGING_ACCOUNT,
  payload: {
    request: {
      method: 'POST',
      url: MESSAGING_ACCOUNTS_API_URL,
      data: account,
    },
  },
  meta,
});

export const updateMessagingAccount = (
  { id, name, productToken, sms }: actionTypes.MessagingAccountUpdatePayload,
  meta: MetaAction = {}
): actionTypes.UpdateMessagingAccountAction => ({
  type: types.UPDATE_MESSAGING_ACCOUNT,
  payload: {
    request: {
      method: 'PUT',
      url: `${MESSAGING_ACCOUNTS_API_URL}/${id}`,
      data: { name, productToken, sms },
    },
  },
  meta,
});

/** Creates or updates, depending on whether the form carries the id of an existing account. */
export const saveMessagingAccount = (
  { provider: _, ...values }: actionTypes.MessagingAccountFormValues,
  meta: MetaAction = {}
): actionTypes.CreateMessagingAccountAction | actionTypes.UpdateMessagingAccountAction => {
  const account = {
    ...values,
    sms: { phoneNumbers: [...new Set(values.sms.phoneNumbers.map(phoneNumber => phoneNumber.trim()))] },
  };
  return 'id' in account ? updateMessagingAccount(account, meta) : createMessagingAccount(account, meta);
};

export const openModal = (accountId?: string, meta: MetaAction = {}): actionTypes.OpenMessagingAccountModalAction => ({
  type: types.OPEN_MODAL,
  payload: accountId,
  meta,
});

export const closeModal = (meta: MetaAction = {}): actionTypes.CloseMessagingAccountModalAction => ({
  type: types.CLOSE_MODAL,
  meta,
});

export const deleteMessagingAccount = (accountId: string, meta: MetaAction = {}): actionTypes.DeleteMessagingAccountAction => ({
  type: types.DELETE_MESSAGING_ACCOUNT,
  payload: {
    accountId,
    request: {
      method: 'DELETE',
      url: `${MESSAGING_ACCOUNTS_API_URL}/${accountId}`,
    },
  },
  meta,
});

export const deleteMessagingAccountSuccess = (accountId: string, meta: MetaAction = {}): actionTypes.DeleteMessagingAccountSuccessAction => ({
  type: types.DELETE_MESSAGING_ACCOUNT_SUCCESS,
  payload: accountId,
  meta,
});

export const deleteMessagingAccountFailure = (error: ApiError, meta: MetaAction = {}): actionTypes.DeleteMessagingAccountFailureAction => ({
  type: types.DELETE_MESSAGING_ACCOUNT_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const fetchAvailableMessagingAccounts = (
  { dealerId, page = 0, name }: actionTypes.FetchAvailableMessagingAccountsOptions,
  meta: MetaAction = {}
): actionTypes.FetchAvailableMessagingAccountsAction => ({
  type: types.FETCH_AVAILABLE_MESSAGING_ACCOUNTS,
  payload: {
    request: {
      method: 'GET',
      url: MESSAGING_ACCOUNTS_FILTER_URL,
      params: {
        availableForDealerId: dealerId,
        page,
        size: AVAILABLE_PAGE_SIZE,
        sort: DEFAULT_SORT,
        ...(name ? { name } : {}),
        paginationType: PaginationType.SLICE,
      },
    },
  },
  meta: { '@app-request': true, ...meta },
});
